import { useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { Search, X } from 'lucide-react'
import { COLUMN_DEFS } from './columns'
import { matchColumns } from './studioColumns'
import type { ColumnId } from './types'

/** Room kept between the panel and the edge of the window or its button. */
const EDGE_PX = 8

/**
 * The panel opened from `anchor`, drawn in the window rather than in the page:
 * above the button, its right edge on the button's. The Command Center's deal
 * table sits in a clipped panel at the foot of the screen, where a panel opened
 * below the button would be cut off.
 */
function aboveAnchor(anchor: HTMLElement): CSSProperties {
  const rect = anchor.getBoundingClientRect()
  return {
    right: Math.max(EDGE_PX, window.innerWidth - rect.right),
    bottom: Math.max(EDGE_PX, window.innerHeight - rect.top + 4),
    maxHeight: Math.max(160, rect.top - 2 * EDGE_PX),
  }
}

/**
 * The grid's "Columns" panel: tick a column to show it, untick it to hide it.
 *
 * Over the Pipeline report's layout, `fixed` names the report's columns. They are
 * what Print, PDF and Excel export, in the report's order, so they are not offered
 * here at all; any other column ticked here is drawn after them, on this screen
 * only (R-12, 29 Oct 2026: "add a column here so that you could pick all different
 * columns", then Last modified to find the deal that just changed).
 */
export function GridColumnPanel({
  visible,
  setVisible,
  fixed,
  anchor,
  onClose,
}: {
  visible: ColumnId[]
  setVisible: (columns: ColumnId[]) => void
  /** The report's columns, when the grid shows the report's layout. */
  fixed?: readonly ColumnId[]
  /** Open above this button, in the window. Absent, the panel drops below its wrapper. */
  anchor?: HTMLElement | null
  onClose: () => void
}) {
  const groups = ['Identity', 'Structure', 'Credit', 'Workflow', 'Governance'] as const
  const locked = new Set<ColumnId>(fixed ?? [])
  // Type to find a column, as in Report Studio (T-65, 2 Nov 2026): the same
  // matcher, so a word finds the same columns in both places.
  const [query, setQuery] = useState('')
  const matched = query.trim()
    ? new Set<ColumnId>(matchColumns(COLUMN_DEFS, [], query).map((column) => column.id))
    : null
  const offered = COLUMN_DEFS.filter((column) => !locked.has(column.id) && (!matched || matched.has(column.id)))
  const panel = (
    <div
      role="dialog"
      aria-label="Columns"
      className={clsx(
        'z-40 flex w-80 flex-col rounded-lg border border-amber-300/15 bg-[#111] p-3 shadow-xl',
        anchor ? 'fixed' : 'absolute right-0 top-8',
      )}
      style={anchor ? aboveAnchor(anchor) : undefined}
    >
      <div className="mb-3 flex shrink-0 items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500">Columns</div>
        <button type="button" aria-label="Close columns" onClick={onClose} className="text-stone-500 hover:text-stone-200">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {fixed ? (
        <p className="mb-2 shrink-0 text-[10px] leading-snug text-stone-500">
          The report&apos;s {fixed.length} columns stay as Print prints them. A column ticked here is
          added after them, on this screen only.
        </p>
      ) : null}
      <label className="mb-2 flex shrink-0 items-center gap-1.5 rounded border border-stone-700 bg-black/40 px-2 py-1">
        <Search className="h-3 w-3 text-stone-500" aria-hidden />
        <input
          aria-label="Search columns"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search columns"
          autoFocus
          className="min-w-0 flex-1 bg-transparent text-[12px] text-stone-100 outline-none placeholder:text-stone-600"
        />
        {query ? (
          <button type="button" aria-label="Clear search" onClick={() => setQuery('')} className="text-stone-500 hover:text-stone-200">
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </label>
      {matched && offered.length === 0 ? (
        <p className="mb-2 text-[11px] text-stone-500">No column matches that.</p>
      ) : null}
      <div className="max-h-80 min-h-0 space-y-3 overflow-y-auto">
        {groups.map((group) => {
          const columns = offered.filter((column) => column.group === group)
          if (columns.length === 0) return null
          return (
            <div key={group}>
              <div className="mb-1 text-[9px] uppercase tracking-[0.14em] text-stone-600">{group}</div>
              <div className="space-y-1">
                {columns.map((column) => {
                  const checked = visible.includes(column.id)
                  return (
                    <label key={column.id} className="flex items-center gap-2 text-[12px] text-stone-300">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (checked && visible.length === 1) return
                          setVisible(
                            checked ? visible.filter((id) => id !== column.id) : [...visible, column.id],
                          )
                        }}
                      />
                      {column.label}
                    </label>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
  return anchor ? createPortal(panel, document.body) : panel
}
